/**
 * Element Type Rules for Semantic Localization Validation
 * 
 * Defines expected patterns for each UI element type based on key suffixes.
 * Used by detectProblematicValue() for semantic quality checks.
 */

export type ElementStyle = 'imperative' | 'noun_phrase' | 'sentence' | 'heading' | 'fragment';

export interface ElementTypeRule {
  /** Key suffixes that match this rule (e.g., ['button', 'action']) */
  suffixes: string[];
  /** Minimum character count */
  minChars: number;
  /** Maximum character count */
  maxChars: number;
  /** Minimum word count */
  minWords: number;
  /** Maximum word count */
  maxWords: number;
  /** Must end with period, exclamation, or question mark */
  mustEndWithPeriod: boolean;
  /** Should NOT end with punctuation */
  forbidPeriod: boolean;
  /** Expected writing style */
  style: ElementStyle;
  /** Example values for reference */
  examples: string[];
}

/**
 * Element type rules ordered by specificity (most specific first)
 */
export const ELEMENT_TYPE_RULES: ElementTypeRule[] = [
  // Actions & Buttons
  {
    suffixes: ['button', 'action', 'cta'],
    minChars: 2,
    maxChars: 35,
    minWords: 1,
    maxWords: 5,
    mustEndWithPeriod: false,
    forbidPeriod: true,
    style: 'imperative',
    examples: ['Save', 'Submit', 'Generate Report', 'Add New Item', 'Learn More']
  },
  
  // Labels & Field Names
  {
    suffixes: ['label', 'field'],
    minChars: 2,
    maxChars: 50,
    minWords: 1,
    maxWords: 6,
    mustEndWithPeriod: false,
    forbidPeriod: true,
    style: 'noun_phrase',
    examples: ['Email', 'Tax Year', 'Company Name', 'Date of Birth']
  },
  
  // Titles & Headings
  {
    suffixes: ['title', 'heading'],
    minChars: 3,
    maxChars: 60,
    minWords: 1,
    maxWords: 10,
    mustEndWithPeriod: false,
    forbidPeriod: true,
    style: 'heading',
    examples: ['Opportunities', 'Profile Settings', 'Dashboard Overview']
  },
  
  // Subtitles
  {
    suffixes: ['subtitle'],
    minChars: 8,
    maxChars: 100,
    minWords: 2,
    maxWords: 15,
    mustEndWithPeriod: false,
    forbidPeriod: false,
    style: 'heading',
    examples: ['Your recent activity', 'Manage your account settings', 'View and edit your preferences']
  },
  
  // Descriptions (longer explanatory text)
  {
    suffixes: ['description', 'desc'],
    minChars: 20,
    maxChars: 300,
    minWords: 4,
    maxWords: 50,
    mustEndWithPeriod: true,
    forbidPeriod: false,
    style: 'sentence',
    examples: [
      'Configure your notification preferences to stay updated on important changes.',
      'This setting controls how your profile appears to other users.'
    ]
  },
  
  // Placeholders
  {
    suffixes: ['placeholder'],
    minChars: 3,
    maxChars: 60,
    minWords: 1,
    maxWords: 8,
    mustEndWithPeriod: false,
    forbidPeriod: true,
    style: 'fragment',
    examples: ['Enter email', 'e.g., 2024', 'Search...', 'Type to search']
  },
  
  // Error Messages
  {
    suffixes: ['error'],
    minChars: 10,
    maxChars: 150,
    minWords: 2,
    maxWords: 25,
    mustEndWithPeriod: true,
    forbidPeriod: false,
    style: 'sentence',
    examples: [
      'Invalid email format. Please enter a valid email address.',
      'This field is required.'
    ]
  },
  
  // Success Messages
  {
    suffixes: ['success'],
    minChars: 10,
    maxChars: 100,
    minWords: 2,
    maxWords: 20,
    mustEndWithPeriod: true,
    forbidPeriod: false,
    style: 'sentence',
    examples: [
      'Your changes have been saved successfully.',
      'Profile updated!'
    ]
  },
  
  // Hints & Tooltips
  {
    suffixes: ['hint', 'tooltip', 'help'],
    minChars: 8,
    maxChars: 200,
    minWords: 2,
    maxWords: 35,
    mustEndWithPeriod: true,
    forbidPeriod: false,
    style: 'sentence',
    examples: [
      'This will be visible to other users.',
      'Click here to learn more about this feature.'
    ]
  },
  
  // Warning & Info Messages
  {
    suffixes: ['warning', 'message', 'info', 'notice'],
    minChars: 8,
    maxChars: 200,
    minWords: 2,
    maxWords: 35,
    mustEndWithPeriod: true,
    forbidPeriod: false,
    style: 'sentence',
    examples: [
      'You have 3 new notifications pending review.',
      'This action cannot be undone.'
    ]
  },
  
  // Confirmations
  {
    suffixes: ['confirm', 'confirmation'],
    minChars: 10,
    maxChars: 150,
    minWords: 3,
    maxWords: 25,
    mustEndWithPeriod: false,
    forbidPeriod: false,
    style: 'sentence',
    examples: [
      'Are you sure you want to delete this item?',
      'This will permanently remove all data.'
    ]
  },
  
  // Empty States
  {
    suffixes: ['empty', 'no_data', 'no_results'],
    minChars: 10,
    maxChars: 150,
    minWords: 2,
    maxWords: 25,
    mustEndWithPeriod: true,
    forbidPeriod: false,
    style: 'sentence',
    examples: [
      'No results found.',
      'You haven\'t added any items yet.'
    ]
  },
  
  // Links & Navigation
  {
    suffixes: ['link', 'nav'],
    minChars: 2,
    maxChars: 40,
    minWords: 1,
    maxWords: 5,
    mustEndWithPeriod: false,
    forbidPeriod: true,
    style: 'noun_phrase',
    examples: ['View All', 'Go to Dashboard', 'Settings', 'Back to Home']
  },
  
  // Tab Names
  {
    suffixes: ['tab'],
    minChars: 2,
    maxChars: 30,
    minWords: 1,
    maxWords: 4,
    mustEndWithPeriod: false,
    forbidPeriod: true,
    style: 'noun_phrase',
    examples: ['Overview', 'Settings', 'Activity Log', 'Team Members']
  },
  
  // Aria Labels (accessibility)
  {
    suffixes: ['aria', 'a11y', 'sr_only'],
    minChars: 5,
    maxChars: 100,
    minWords: 2,
    maxWords: 15,
    mustEndWithPeriod: false,
    forbidPeriod: true,
    style: 'sentence',
    examples: ['Close dialog', 'Open navigation menu', 'Toggle dark mode']
  }
];

/**
 * Find the matching element type rule for a key suffix
 */
export function findElementTypeRule(keySuffix: string): ElementTypeRule | null {
  const lowerSuffix = keySuffix.toLowerCase();
  
  return ELEMENT_TYPE_RULES.find(rule =>
    rule.suffixes.some(suffix => 
      lowerSuffix === suffix || 
      lowerSuffix.endsWith(`_${suffix}`) ||
      lowerSuffix.endsWith(`-${suffix}`)
    )
  ) || null;
}

/**
 * Get human-readable name for element style
 */
export function getStyleLabel(style: ElementStyle): string {
  const labels: Record<ElementStyle, string> = {
    imperative: 'Action verb (e.g., "Save", "Submit")',
    noun_phrase: 'Noun phrase (e.g., "Email", "Tax Year")',
    sentence: 'Complete sentence with punctuation',
    heading: 'Title case heading',
    fragment: 'Short phrase or fragment'
  };
  return labels[style];
}
